/**
 * Acoustic onset DETECTION — where energy appears in a new part of the
 * spectrum, i.e. where a syllable starts.
 *
 * THIS IS LIVE CODE ON THE UI PATH. It was extracted from `onsets.ts`, whose
 * other half — the dynamic program that lays a burst's segments onto these
 * candidates — is a legacy repair stage and now lives in
 * `services/legacy/onsetAlign.ts`. The split exists because detection has
 * consumers that have nothing to do with the legacy pipeline:
 * `components/taskAudio.ts` builds the waveform drag magnets on CorrectPage and
 * CompletedPage out of `detectOnsets`. Quarantining the whole file would have
 * taken that with it.
 *
 * HOW
 * ---
 * A SuperFlux-style spectral-flux detector over the decoded audio: log-spaced
 * bands, log-compressed magnitudes, half-wave-rectified difference against a
 * max-filtered earlier frame, then peak picking against a sliding median +
 * k·MAD threshold. On the calibration file the onset count per burst tracks the
 * true word count closely (the seven-word burst at 28.53–31.08 s yields 8
 * peaks; the one-word burst at 1.22–2.74 s yields 1).
 *
 * SHAPES
 * ------
 * The span/label/envelope interfaces below are shared with `legacy/onsetAlign`
 * and with `legacy/burstGroups`, which is why they live on this side of the
 * split rather than with the consumer that uses most of them.
 *
 * DEPENDENCIES
 * ------------
 * None. Every function below is pure over Float32Arrays and plain objects,
 * including a small radix-2 FFT, so the module can be exercised in a Node
 * harness with no browser and no audio library.
 */

/* ------------------------------------------------------------------ */
/* Shapes                                                             */
/* ------------------------------------------------------------------ */

/** Anything with boundaries and a label. `RawSegment` and `Segment` both fit. */
export interface LabelledSpan {
  startSec: number;
  endSec: number;
  heard: string;
  kind: string;
}

/** One continuous stretch of speech, bounded by real silence. */
export interface SpanSec {
  startSec: number;
  endSec: number;
}

/** The VAD output this module needs. `SpeechEnvelope` is structurally this. */
export interface EnvelopeLike {
  rms: ArrayLike<number>;
  mask: ArrayLike<number>;
  hopSec: number;
}

/** A place a word could plausibly start. */
export interface OnsetCandidate {
  timeSec: number;
  /** 0..1. Flux peaks carry real strength; anchors are evidence-free at 0. */
  strength: number;
  source: 'flux' | 'dip' | 'anchor';
}

export interface OnsetTrack {
  /** Seconds per flux frame. */
  hopSec: number;
  /** Normalised spectral flux, one value per frame. */
  flux: Float32Array;
  /** Picked peaks, ascending in time. */
  onsets: OnsetCandidate[];
}

/* ------------------------------------------------------------------ */
/* 1 — spectral flux                                                   */
/* ------------------------------------------------------------------ */

export interface OnsetOptions {
  /** Flux frame hop, seconds. 5 ms resolves the 70 ms word rate comfortably. */
  hopSec?: number;
  /** Analysis window, seconds. */
  winSec?: number;
  /** Rate the signal is decimated to before analysis. */
  workRate?: number;
  /** Log-spaced bands between {@link minFreqHz} and {@link maxFreqHz}. */
  bands?: number;
  minFreqHz?: number;
  maxFreqHz?: number;
  /** Frames of lag for the flux difference. 2 frames = 10 ms. */
  lagFrames?: number;
  /** Half-width of the sliding median/MAD window, seconds. */
  thresholdRadiusSec?: number;
  /** Peak must exceed median + k·MAD. */
  thresholdK?: number;
  /** Minimum spacing between accepted peaks, seconds. */
  minSeparationSec?: number;
  /** Peak must also exceed this fraction of the file's 98th-percentile flux. */
  floorFrac?: number;
}

/*
 * Exported ONLY so `legacy/onsetAlign.detectDips` can share
 * `minSeparationSec`: dips and flux peaks are picked off the same audio and a
 * second copy of that number would let the two drift apart silently.
 */
export const ONSET_DEFAULTS = {
  hopSec: 0.005,
  winSec: 0.032,
  workRate: 16000,
  bands: 24,
  minFreqHz: 80,
  maxFreqHz: 8000,
  lagFrames: 2,
  thresholdRadiusSec: 0.15,
  thresholdK: 1.4,
  minSeparationSec: 0.07,
  floorFrac: 0.06,
} as const;

/** Smallest power of two at or above `n`. */
function nextPow2(n: number): number {
  let p = 1;
  while (p < n) p <<= 1;
  return p;
}

/**
 * In-place iterative radix-2 complex FFT.
 *
 * Written out rather than pulled in because this file is deliberately
 * dependency-free and the transform is 30 lines. Sizes here are 512 or 1024,
 * so the naive twiddle tables cost nothing.
 */
function fft(re: Float64Array, im: Float64Array): void {
  const n = re.length;
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      let t = re[i];
      re[i] = re[j];
      re[j] = t;
      t = im[i];
      im[i] = im[j];
      im[j] = t;
    }
  }
  for (let len = 2; len <= n; len <<= 1) {
    const ang = (-2 * Math.PI) / len;
    const wRe = Math.cos(ang);
    const wIm = Math.sin(ang);
    for (let i = 0; i < n; i += len) {
      let curRe = 1;
      let curIm = 0;
      for (let k = 0; k < len / 2; k++) {
        const uRe = re[i + k];
        const uIm = im[i + k];
        const vRe = re[i + k + len / 2] * curRe - im[i + k + len / 2] * curIm;
        const vIm = re[i + k + len / 2] * curIm + im[i + k + len / 2] * curRe;
        re[i + k] = uRe + vRe;
        im[i + k] = uIm + vIm;
        re[i + k + len / 2] = uRe - vRe;
        im[i + k + len / 2] = uIm - vIm;
        const nextRe = curRe * wRe - curIm * wIm;
        curIm = curRe * wIm + curIm * wRe;
        curRe = nextRe;
      }
    }
  }
}

/** Cheap linear decimation. Onsets need no better than 16 kHz. */
function decimate(input: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (toRate >= fromRate) return input;
  const ratio = fromRate / toRate;
  const out = new Float32Array(Math.max(1, Math.floor(input.length / ratio)));
  for (let i = 0; i < out.length; i++) {
    const x = i * ratio;
    const i0 = Math.floor(x);
    const i1 = Math.min(input.length - 1, i0 + 1);
    const f = x - i0;
    out[i] = input[i0] * (1 - f) + input[i1] * f;
  }
  return out;
}

/**
 * SuperFlux onset strength.
 *
 * Three details that matter and are easy to get wrong:
 *  - LOG COMPRESSION. Raw magnitudes make the detector deaf to a quiet word
 *    after a loud one; `log(1 + γ|X|)` puts a whisper and a shout on comparable
 *    footing, which is the whole problem inside a burst.
 *  - MAX FILTER over the neighbouring bands of the LAGGED frame. Without it a
 *    steady vowel with any pitch movement produces flux every frame and the
 *    peak picker drowns.
 *  - A LAG of two frames rather than one. At a 5 ms hop, consecutive frames
 *    overlap almost completely and their difference is mostly noise.
 */
export function computeOnsetFlux(
  mono: Float32Array,
  sampleRate: number,
  options: OnsetOptions = {},
): { flux: Float32Array; hopSec: number } {
  const hopSec = options.hopSec ?? ONSET_DEFAULTS.hopSec;
  const winSec = options.winSec ?? ONSET_DEFAULTS.winSec;
  const workRate = Math.min(sampleRate, options.workRate ?? ONSET_DEFAULTS.workRate);
  const bandCount = options.bands ?? ONSET_DEFAULTS.bands;
  const minHz = options.minFreqHz ?? ONSET_DEFAULTS.minFreqHz;
  const maxHz = Math.min(options.maxFreqHz ?? ONSET_DEFAULTS.maxFreqHz, workRate / 2);
  const lag = Math.max(1, options.lagFrames ?? ONSET_DEFAULTS.lagFrames);

  const x = decimate(mono, sampleRate, workRate);
  const hop = Math.max(1, Math.round(workRate * hopSec));
  const win = Math.max(hop * 2, Math.round(workRate * winSec));
  const n = nextPow2(win);
  const frames = Math.max(1, Math.floor((x.length - win) / hop) + 1);

  // Hann window, and the band edges as bin indices.
  const window = new Float64Array(win);
  for (let i = 0; i < win; i++) window[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (win - 1));

  const binHz = workRate / n;
  const edges = new Int32Array(bandCount + 1);
  for (let b = 0; b <= bandCount; b++) {
    const hz = minHz * Math.pow(maxHz / minHz, b / bandCount);
    edges[b] = Math.min(n >> 1, Math.max(1, Math.round(hz / binHz)));
  }
  for (let b = 1; b <= bandCount; b++) if (edges[b] <= edges[b - 1]) edges[b] = edges[b - 1] + 1;

  const spec = new Float32Array(frames * bandCount);
  const re = new Float64Array(n);
  const im = new Float64Array(n);

  for (let t = 0; t < frames; t++) {
    re.fill(0);
    im.fill(0);
    const from = t * hop;
    for (let i = 0; i < win && from + i < x.length; i++) re[i] = x[from + i] * window[i];
    fft(re, im);
    const base = t * bandCount;
    for (let b = 0; b < bandCount; b++) {
      let sum = 0;
      const lo = edges[b];
      const hi = Math.min(edges[b + 1], n >> 1);
      for (let k = lo; k < hi; k++) sum += Math.hypot(re[k], im[k]);
      // Log compression: a quiet word after a loud one must still show an onset.
      spec[base + b] = Math.log1p(20 * (sum / Math.max(1, hi - lo)));
    }
  }

  const flux = new Float32Array(frames);
  for (let t = lag; t < frames; t++) {
    const cur = t * bandCount;
    const prev = (t - lag) * bandCount;
    let sum = 0;
    for (let b = 0; b < bandCount; b++) {
      // max filter over ±1 band of the lagged frame — vibrato suppression
      let ref = spec[prev + b];
      if (b > 0 && spec[prev + b - 1] > ref) ref = spec[prev + b - 1];
      if (b + 1 < bandCount && spec[prev + b + 1] > ref) ref = spec[prev + b + 1];
      const d = spec[cur + b] - ref;
      if (d > 0) sum += d;
    }
    flux[t] = sum;
  }

  // Normalise by the 98th percentile so `strength` means the same thing on a
  // quiet phone recording and a hot studio take.
  const sorted = Float32Array.from(flux).sort();
  const p98 = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.98))] || 1;
  for (let t = 0; t < frames; t++) flux[t] = flux[t] / p98;

  return { flux, hopSec };
}

/** Median of a slice, via a copy. Windows here are ~60 values. */
function medianOf(values: Float32Array, from: number, to: number): number {
  const slice = values.slice(Math.max(0, from), Math.min(values.length, to));
  if (slice.length === 0) return 0;
  const sorted = Float32Array.from(slice).sort();
  return sorted[sorted.length >> 1];
}

/**
 * Peak picking against a LOCAL threshold.
 *
 * A fixed threshold cannot work on connected speech: the flux floor inside a
 * loud vowel is higher than the peaks of a quiet aside. Median + k·MAD over a
 * ±150 ms window adapts to that, and the absolute floor stops the detector from
 * finding "onsets" in room tone where the local median is essentially zero.
 */
export function pickOnsetPeaks(
  flux: Float32Array,
  hopSec: number,
  options: OnsetOptions = {},
): OnsetCandidate[] {
  const radius = Math.max(1, Math.round((options.thresholdRadiusSec ?? ONSET_DEFAULTS.thresholdRadiusSec) / hopSec));
  const k = options.thresholdK ?? ONSET_DEFAULTS.thresholdK;
  const minSep = Math.max(1, Math.round((options.minSeparationSec ?? ONSET_DEFAULTS.minSeparationSec) / hopSec));
  const floorFrac = options.floorFrac ?? ONSET_DEFAULTS.floorFrac;
  const localMaxRadius = Math.max(1, Math.round(0.015 / hopSec));

  const raw: Array<{ frame: number; strength: number }> = [];
  for (let t = 1; t < flux.length - 1; t++) {
    const v = flux[t];
    if (v < floorFrac) continue;

    let isMax = true;
    for (let d = -localMaxRadius; d <= localMaxRadius && isMax; d++) {
      const u = t + d;
      if (u < 0 || u >= flux.length || u === t) continue;
      if (flux[u] > v) isMax = false;
    }
    if (!isMax) continue;

    const med = medianOf(flux, t - radius, t + radius + 1);
    let mad = 0;
    let count = 0;
    for (let u = Math.max(0, t - radius); u < Math.min(flux.length, t + radius + 1); u++) {
      mad += Math.abs(flux[u] - med);
      count++;
    }
    mad = count > 0 ? mad / count : 0;
    if (v < med + k * mad) continue;

    raw.push({ frame: t, strength: v });
  }

  // Enforce the minimum separation strongest-first, so a real onset is never
  // suppressed by a weaker neighbour that happened to come earlier.
  raw.sort((a, b) => b.strength - a.strength);
  const taken: number[] = [];
  const kept: Array<{ frame: number; strength: number }> = [];
  for (const p of raw) {
    if (taken.some((f) => Math.abs(f - p.frame) < minSep)) continue;
    taken.push(p.frame);
    kept.push(p);
  }
  kept.sort((a, b) => a.frame - b.frame);

  return kept.map((p) => ({
    timeSec: p.frame * hopSec,
    strength: Math.min(1, p.strength),
    source: 'flux' as const,
  }));
}

/** Flux + peak picking in one call. */
export function detectOnsets(
  mono: Float32Array,
  sampleRate: number,
  options: OnsetOptions = {},
): OnsetTrack {
  const { flux, hopSec } = computeOnsetFlux(mono, sampleRate, options);
  return { flux, hopSec, onsets: pickOnsetPeaks(flux, hopSec, options) };
}
